// src/components/pages/benefaction/BenefactorSignUp.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

// Services — التحقق يقين ثم OTP ثم donation login ثم إنشاء المتبرع عبر /api/donation/donor/create
import { startDonorEmailOtp, createDonor, listMine, scopeDonorsToCurrentUser } from "../../../services/donorService";
import { verifyPublic, type VerifyPublicResult } from "../../../services/yaqeenService";
import { donationLogin, isAuthenticated, getIdentityFromToken, clearAuthToken } from "../../../services/authService";
import { RECAPTCHA_SITE_KEY } from "../../../config";
import { inputBaseCls, inputReadOnlyCls, labelCls, helpCls } from "../../../utils/formStyles";

/* ------------------------- helpers (unchanged) ------------------------- */
const RESEND_COOLDOWN_S = 60;
const onlyDigits = (s: string) => (s || "").replace(/\D+/g, "");
const isCitizenId = (nin: string) => /^\s*1/.test(nin);
const isResidentId = (nin: string) => /^\s*[23]/.test(nin);

// Normalize KSA phone -> 9665XXXXXXXX
const normPhoneKsa = (input: string) => {
  const d = onlyDigits(input);
  if (d.startsWith("05") && d.length === 10) return "966" + d.slice(1);
  if (d.startsWith("9665") && (d.length === 12 || d.length === 13)) return d.slice(0, 12);
  return "";
};

// Accept yyyy / yyyy-MM / yyyy-MM-dd and normalize to yyyy-MM-dd
const toYyyyMmDd = (iso?: string | null) => {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/);
  if (!m) return "";
  const y = m[1];
  const mm = m[2] || "01";
  const dd = m[3] || "01";
  return `${y}-${mm}-${dd}`;
};

const toHijriYearMonth = (value?: string | null) => {
  if (!value) return "";
  const m = String(value).trim().match(/^(\d{4})-(\d{2})/);
  if (!m) return "";
  return `${m[1]}-${m[2]}`;
};

type ProfileSnapshot = {
  name?: string;
  nationalId?: string;
  phone?: string;
  email?: string;
  countryName?: string;
  dob?: string;
  dobHijri?: string;
};

function mergeProfileWithYaqeen(
  profile: ProfileSnapshot,
  verified: VerifyPublicResult
): ProfileSnapshot {
  const dobGregorian = toYyyyMmDd(verified.dobGregorian);
  const dobHijri = (verified.dobHijri ?? "").toString().trim();
  return {
    ...profile,
    name: verified.nameAr || profile.name,
    countryName: verified.countryName || profile.countryName,
    dob: dobGregorian || profile.dob,
    dobHijri: dobHijri || profile.dobHijri,
  };
}

// Hijri lists for citizens (year + month)
function useHijriLists() {
  const now = new Date().getFullYear();
  const approxHijri = now - 579;
  const years = useMemo(
    () => Array.from({ length: 150 }, (_, i) => String(approxHijri - i)),
    [approxHijri]
  );
  const months: Array<[string, string]> = useMemo(
    () => [
      ["01", "محرم"],
      ["02", "صفر"],
      ["03", "ربيع الأول"],
      ["04", "ربيع الثاني"],
      ["05", "جمادى الأولى"],
      ["06", "جمادى الآخرة"],
      ["07", "رجب"],
      ["08", "شعبان"],
      ["09", "رمضان"],
      ["10", "شوال"],
      ["11", "ذو القعدة"],
      ["12", "ذو الحجة"],
    ],
    []
  );
  return { years, months };
}

/* =========================== Component =========================== */
export default function BenefactorSignUp() {
  const dir = "rtl"; // Hardcoded for Arabic
  const rtl = true; // Hardcoded for Arabic
  const navigate = useNavigate();
  const location = useLocation();

  // optional continue token
  const cont = useMemo(() => {
    const u = new URL(window.location.href);
    return u.searchParams.get("c") || undefined;
  }, [location.search]);

  // صفحة العودة بعد إكمال التسجيل (من بوب أب حساب متبرع مطلوب)
  const returnTo = useMemo(() => {
    const u = new URL(window.location.href);
    const p = u.searchParams.get("returnTo")?.trim();
    if (!p || !p.startsWith("/") || p.startsWith("//")) return null;
    return p;
  }, [location.search]);

  // step control: "add-donor" = مسجل Portal ويريد إضافة صلاحية متبرع
  const [step, setStep] = useState<"check" | "identity" | "phone" | "add-donor" | "done">("check");

  // Identity inputs
  const [nationalId, setNationalId] = useState("");
  const [hijriY, setHijriY] = useState("");
  const [hijriM, setHijriM] = useState("");
  const [gregDate, setGregDate] = useState(""); // resident -> yyyy-MM-dd
  const [captcha1, setCaptcha1] = useState<string | null>(null);
  const recaptcha1Ref = useRef<ReCAPTCHA>(null);

  // Verified info from Yaqeen
  const [verifiedName, setVerifiedName] = useState("");
  const [dobGregorian, setDobGregorian] = useState(""); // always yyyy-MM-dd
  const [gender, setGender] = useState<"male" | "female" | "">("");
  const [genderLocked, setGenderLocked] = useState(false);
  const [verifiedCountryName, setVerifiedCountryName] = useState("");
  const [verifiedRegionName, setVerifiedRegionName] = useState("");

  // Phone + OTP
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [captcha2, setCaptcha2] = useState<string | null>(null);
  const recaptcha2Ref = useRef<ReCAPTCHA>(null);
  const [showResendSection, setShowResendSection] = useState(false);

  // misc
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [addDonorProfile, setAddDonorProfile] = useState<ProfileSnapshot | null>(null);
  const [upgradeVerificationLoading, setUpgradeVerificationLoading] = useState(false);
  const [upgradeVerificationError, setUpgradeVerificationError] = useState<string | null>(null);
  const { years: hijriYears, months: hijriMonths } = useHijriLists();

  const citizen = isCitizenId(nationalId);
  const resident = isResidentId(nationalId);

  // cooldown ticker (unchanged)
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // فحص: إن كان المستخدم مسجلاً وليس متبرعاً → عرض "إضافة متبرع". غير مسجل → توجيه لـ Portal.
  useEffect(() => {
    if (step !== "check") return;
    (async () => {
      if (!isAuthenticated()) {
        const signupReturn = returnTo ? "/benefaction/signup?returnTo=" + encodeURIComponent(returnTo) : "/benefaction/signup";
        navigate("/resident-signup?returnTo=" + encodeURIComponent(signupReturn), { replace: true });
        return;
      }
      try {
        const donors = scopeDonorsToCurrentUser(await listMine());
        if (donors && donors.length > 0) {
          navigate(returnTo || "/benefaction/lands", { replace: true });
          return;
        }
        setStep("add-donor");
      } catch {
        setStep("add-donor");
      }
    })();
  }, [step, navigate, returnTo]);

  // تحميل بيانات الملف التعريفي عند عرض خطوة إضافة المتبرع
  useEffect(() => {
    if (step !== "add-donor") return;
    let cancelled = false;
    setAcknowledged(false);
    setUpgradeVerificationError(null);
    setUpgradeVerificationLoading(false);

    (async () => {
      let merged: ProfileSnapshot = {};
      try {
        const raw = localStorage.getItem("residentPartnerProfile");
        const profile = raw ? JSON.parse(raw) : null;
        const identity = getIdentityFromToken();
        merged = profile && typeof profile === "object" ? { ...profile } : {};
        if (identity?.nationalId && !merged.nationalId) merged.nationalId = identity.nationalId;
        if (identity?.phone && !merged.phone) merged.phone = identity.phone;
        setAddDonorProfile(Object.keys(merged).length > 0 ? merged : null);
      } catch {
        setAddDonorProfile(null);
        return;
      }

      const nin = onlyDigits(merged.nationalId || "").slice(0, 10);
      if (!nin) return;

      const verifyDob = isCitizenId(nin)
        ? toHijriYearMonth(merged.dobHijri)
        : toYyyyMmDd(merged.dob);

      if (!verifyDob) {
        setUpgradeVerificationError(
          isCitizenId(nin)
            ? "تعذر إعادة جلب البيانات من يقين لأن تاريخ الميلاد الهجري غير متوفر في بيانات الحساب الحالي."
            : "تعذر إعادة جلب البيانات من يقين لأن تاريخ الميلاد الميلادي غير متوفر في بيانات الحساب الحالي."
        );
        return;
      }

      setUpgradeVerificationLoading(true);
      try {
        const verified = await verifyPublic({ identificationId: nin, dateOfBirth: verifyDob });
        if (cancelled) return;

        const refreshed = mergeProfileWithYaqeen(merged, verified);
        setAddDonorProfile(Object.keys(refreshed).length > 0 ? refreshed : null);
        setVerifiedName(verified.nameAr || "");
        setVerifiedCountryName(verified.countryName ?? "");
        setDobGregorian(toYyyyMmDd(verified.dobGregorian) || "");

        try {
          localStorage.setItem("residentPartnerProfile", JSON.stringify(refreshed));
        } catch {
          /* ignore storage errors */
        }
      } catch (ex: any) {
        if (cancelled) return;
        setUpgradeVerificationError(
          ex?.message || "تعذر إعادة التحقق من البيانات عبر خدمة يقين. يمكنك المتابعة عبر التحقق الكامل."
        );
      } finally {
        if (!cancelled) setUpgradeVerificationLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [step]);

  /* --------------------- إضافة صلاحية المتبرع (للمسجلين) --------------------- */
  async function onAddDonor(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);

    if (upgradeVerificationLoading) {
      setErr("ما زال يتم تحديث بياناتك من يقين. الرجاء الانتظار قليلًا ثم إعادة المحاولة.");
      return;
    }

    let profile: ProfileSnapshot = addDonorProfile ?? {};
    if (!Object.keys(profile).length) {
      try {
        const stored = localStorage.getItem("residentPartnerProfile");
        if (stored) profile = JSON.parse(stored);
      } catch {}
    }

    const identity = getIdentityFromToken();
    const ninRaw = profile.nationalId || identity?.nationalId || "";
    const nin = ninRaw.replace(/\D/g, "").slice(0, 10);
    if (!nin) {
      setErr("لم نتمكن من الحصول على رقم الهوية. الرجاء تسجيل الدخول مرة أخرى أو إكمال التسجيل كزائر أولاً.");
      return;
    }

    if (isCitizenId(nin) && !profile.dobHijri) {
      setErr("تعذر إنشاء حساب المتبرع لأن تاريخ الميلاد الهجري غير متوفر. يرجى إعادة التحقق عبر خدمة يقين أولًا.");
      return;
    }

    const p = profile.phone || identity?.phone || normPhoneKsa(phone) || "";
    setBusy(true);
    try {
      await createDonor({
        name: profile.name || verifiedName || "",
        nationalId: nin,
        email: profile.email || (email || "").trim() || undefined,
        phone: p || undefined,
        dob: profile.dob || dobGregorian || undefined,
        dobHijri: profile.dobHijri || undefined,
        countryName: profile.countryName || verifiedCountryName || undefined,
        DonorType: "direct",
        creation_platform: "donation_web_portal",
      });
      setStep("done");
      setTimeout(() => navigate(returnTo || "/benefaction/lands", { replace: true }), 900);
    } catch (ex: any) {
      const data = ex?.response?.data;
      const msg =
        (typeof data?.detail === "string" && data.detail) ||
        (typeof data?.message === "string" && data.message) ||
        ex?.message ||
        "حدث خطأ أثناء إضافة صلاحية المتبرع. يرجى المحاولة مرة أخرى";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  /* --------------------- Step 1: Verify identity (unchanged) --------------------- */
  async function onVerifyIdentity(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);

    const nin = onlyDigits(nationalId);
    if (nin.length !== 10) return setErr("الرجاء إدخال رقم الهوية الوطنية (10 أرقام)");
    if (!captcha1) return setErr("الرجاء إكمال التحقق من reCAPTCHA");

    // citizen -> send Hijri yyyy-MM ; resident -> send Gregorian yyyy-MM-dd
    const dobInput = citizen ? (hijriY && hijriM ? `${hijriY}-${hijriM}` : "") : gregDate;
    if (!dobInput) return setErr(citizen ? "الرجاء إدخال تاريخ الميلاد (هجري)" : "الرجاء إدخال تاريخ الميلاد (ميلادي)");

    try {
      setBusy(true);
      const v = await verifyPublic({ identificationId: nin, dateOfBirth: dobInput });

      // name
      setVerifiedName(v.nameAr || "");

      // gender: بعد التحقق من يقين الجنس للعرض فقط ولا يُغيّر
      if (v.gender === "male" || v.gender === "female") {
        setGender(v.gender);
      } else {
        setGender("");
      }
      setGenderLocked(true);

      // اسم البلد والمنطقة من يقين (للعرض فقط)
      setVerifiedCountryName(v.countryName ?? "");
      setVerifiedRegionName(v.regionName ?? "");

      // Always store Gregorian yyyy-MM-dd for DB
      const greg = toYyyyMmDd(v.dobGregorian);
      setDobGregorian(greg || "");

      setStep("phone");
    } catch (ex: any) {
      setErr(ex?.message || "حدث خطأ أثناء التحقق من الهوية. يرجى المحاولة مرة أخرى");
    } finally {
      setBusy(false);
      recaptcha1Ref.current?.reset();
      setCaptcha1(null);
    }
  }

  /* --------------------- Step 2: إرسال رمز التحقق إلى البريد (يتوافق مع السيرفر فينشئ الحساب) --------------------- */
  async function onSendOtp() {
    setErr(null);
    const emailTrim = (email || "").trim();
    const p = normPhoneKsa(phone);
    if (!emailTrim) return setErr("الرجاء إدخال البريد الإلكتروني");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) return setErr("الرجاء إدخال بريد إلكتروني صحيح");
    if (!p) return setErr("الرجاء إدخال رقم هاتف صحيح (مثال: 05XXXXXXXX)");
    if (!captcha2) return setErr("الرجاء إكمال التحقق من reCAPTCHA");

    try {
      setBusy(true);
      const r = await startDonorEmailOtp(emailTrim, captcha2, cont, p);
      if (!r.ok) {
        if (/REQUEST_TIMEOUT|ECONNABORTED|timeout/i.test(r.message || "")) {
          setErr("انتهت مهلة الطلب. يرجى المحاولة مرة أخرى");
        } else if (r.message === "AUTH_401") {
          setErr("انتهت الجلسة. الرجاء المحاولة مرة أخرى");
        } else if (/invalid_phone|phone/i.test(r.message || "")) {
          setErr("رقم الهاتف غير صحيح أو غير مقبول. تحقق من الرقم (مثال: 05XXXXXXXX) وأعد المحاولة.");
        } else {
          setErr(r.message || "حدث خطأ أثناء إرسال رمز التحقق. يرجى المحاولة مرة أخرى");
        }
        return;
      }
      setOtpSent(true);
      setCooldown(r.cooldownSeconds ?? RESEND_COOLDOWN_S);
    } catch (ex: any) {
      const msg = (ex?.message || "").toLowerCase();
      if (/invalid_phone|phone/i.test(msg)) {
        setErr("رقم الهاتف غير صحيح أو غير مقبول. تحقق من الرقم (مثال: 05XXXXXXXX) وأعد المحاولة.");
      } else {
        setErr("حدث خطأ أثناء إرسال رمز التحقق إلى البريد الإلكتروني. يرجى المحاولة مرة أخرى");
      }
    } finally {
      setBusy(false);
      recaptcha2Ref.current?.reset();
      setCaptcha2(null);
    }
  }

  /* --------------------- Step 2: Submit signup --------------------- */
  async function onSubmitSignup(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);

    const nin = onlyDigits(nationalId);
    const p = normPhoneKsa(phone);
    const emailTrim = (email || "").trim();
    if (!nin) return setErr("الرجاء إدخال رقم الهوية الوطنية");
    if (!p) return setErr("الرجاء إدخال رقم هاتف صحيح");
    if (!emailTrim) return setErr("الرجاء إدخال البريد الإلكتروني");
    if (!otp || !/^\d{1,6}$/.test(otp.trim())) return setErr("يرجى إدخال رمز التحقق المرسل إلى بريدك (من 1 إلى 6 أرقام)");
    if (!dobGregorian) return setErr("تاريخ الميلاد مطلوب");

    try {
      setBusy(true);

      // مسح أي توكن قديم قبل الحصول على توكن جديد
      clearAuthToken();

      // 1) تسجيل الدخول بالـ OTP للحصول على JWT (res_partner_id من الباكند)
      await donationLogin({
        identifier: emailTrim,
        phone: p,
        email: emailTrim,
        nationalId: nin,
        code: otp.trim(),
        continueToken: cont ?? undefined,
      });

      // تأخير قصير لإعطاء الباك إند وقتاً لتهيئة الجلسة (يُجنّب Timeout)
      await new Promise((r) => setTimeout(r, 500));

      // 2) إنشاء سجل المتبرع عبر POST /api/donation/donor/create (يطابق Swagger)
      await createDonor({
        name: verifiedName || "",
        nationalId: nin,
        email: emailTrim || undefined,
        phone: p,
        dob: dobGregorian,
        dobHijri: citizen ? (hijriY && hijriM ? `${hijriY}-${hijriM}` : undefined) : undefined,
        DonorType: "direct",
        creation_platform: "donation_web_portal",
      });

      setStep("done");
      setTimeout(() => navigate(returnTo || "/benefaction/lands", { replace: true }), 900);
    } catch (ex: any) {
      const data = ex?.response?.data;
      const msg =
        (typeof data?.detail === "string" && data.detail) ||
        (typeof data?.message === "string" && data.message) ||
        (typeof data?.error === "string" && data.error) ||
        ex?.message ||
        "حدث خطأ أثناء إنشاء الحساب. يرجى المحاولة مرة أخرى";

      const isExpired = (msg || "").toLowerCase().includes("expired");
      const isAuth401 = msg === "AUTH_401" || (ex as any)?.code === "AUTH_401";
      setErr(
        isAuth401
          ? "انتهت الجلسة. الرجاء المحاولة مرة أخرى."
          : isExpired
          ? "انتهت صلاحية رمز التحقق. اضغط «إعادة إرسال رمز التحقق» أدناه ثم أدخل الرمز الجديد خلال دقائق."
          : msg
      );
    } finally {
      setBusy(false);
    }
  }

  /* ------------------------------- UI ------------------------------- */
  const cardCls =
    "rounded-2xl border border-sa-200 bg-white shadow-sm ring-1 ring-black/5";
  const sectionTitleCls = "text-base font-semibold text-sa-700";

  return (
    <main dir={dir} className="mx-auto w-full max-w-3xl px-4 py-6 lg:max-w-4xl">
      {/* Page Title */}
      <header className="mb-5">
        <h1 className="text-2xl font-bold leading-tight text-sa-900 text-center">
          {step === "add-donor" ? "إنشاء حساب متبرع" : step === "check" ? "جاري التحقق..." : "إنشاء حساب جديد"}
        </h1>
      </header>

      {/* STEP: جاري التحقق */}
      {step === "check" && (
        <div className={cardCls + " p-8 text-center text-sa-700"}>
          جاري التحقق من حسابك...
        </div>
      )}

      {/* STEP: إضافة متبرع (للمسجلين) — عرض البيانات + الإقرار + زر إنشاء */}
      {step === "add-donor" && (
        <form onSubmit={onAddDonor} className={cardCls}>
          <div className="p-5 lg:p-6">
            <h2 className={sectionTitleCls + " mb-4"}>إنشاء حساب متبرع</h2>

            {!addDonorProfile || !(addDonorProfile.nationalId || getIdentityFromToken()?.nationalId) ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  لا توجد بيانات ملف تعريفي كافية. لإضافة صلاحية المتبرع، سجّل كاملاً عبر خدمة يقين ورمز التحقق.
                </div>
                <button
                  type="button"
                  onClick={() => setStep("identity")}
                  className="inline-flex items-center justify-center rounded-xl bg-sa-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sa-700"
                >
                  تسجيل كامل (يقين + OTP)
                </button>
              </div>
            ) : (
              <>
                {upgradeVerificationLoading && (
                  <div className="mb-4 rounded-xl border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-800">
                    جاري إعادة جلب البيانات من خدمة يقين لتحديث معلومات الحساب قبل الترقية...
                  </div>
                )}

                {upgradeVerificationError && (
                  <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <div>{upgradeVerificationError}</div>
                    <button
                      type="button"
                      onClick={() => setStep("identity")}
                      className="mt-3 inline-flex items-center justify-center rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
                    >
                      إعادة التحقق عبر يقين
                    </button>
                  </div>
                )}

                <div className="mb-5 rounded-xl border border-neutral-200 bg-neutral-50 overflow-hidden">
                  <div className="border-b border-neutral-200 px-4 py-2.5 bg-white">
                    <h3 className="text-sm font-semibold text-neutral-800">البيانات المستخدمة</h3>
                  </div>
                  <dl className="divide-y divide-neutral-100">
                    {addDonorProfile?.name && (
                      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-4 py-3">
                        <dt className="text-neutral-500 text-sm font-medium">الاسم</dt>
                        <dd className="text-neutral-900">{addDonorProfile.name}</dd>
                      </div>
                    )}
                    {(addDonorProfile?.nationalId || getIdentityFromToken()?.nationalId) && (
                      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-4 py-3">
                        <dt className="text-neutral-500 text-sm font-medium">رقم الهوية الوطنية</dt>
                        <dd className="text-neutral-900 font-mono">{addDonorProfile?.nationalId || getIdentityFromToken()?.nationalId}</dd>
                      </div>
                    )}
                    {(addDonorProfile?.phone || getIdentityFromToken()?.phone) && (
                      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-4 py-3">
                        <dt className="text-neutral-500 text-sm font-medium">رقم الجوال</dt>
                        <dd className="text-neutral-900">{addDonorProfile?.phone || getIdentityFromToken()?.phone}</dd>
                      </div>
                    )}
                    {addDonorProfile?.email && (
                      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-4 py-3">
                        <dt className="text-neutral-500 text-sm font-medium">البريد الإلكتروني</dt>
                        <dd className="text-neutral-900">{addDonorProfile.email}</dd>
                      </div>
                    )}
                    {addDonorProfile?.countryName && (
                      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-4 py-3">
                        <dt className="text-neutral-500 text-sm font-medium">البلد</dt>
                        <dd className="text-neutral-900">{addDonorProfile.countryName}</dd>
                      </div>
                    )}
                    {addDonorProfile?.dob && (
                      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-4 py-3">
                        <dt className="text-neutral-500 text-sm font-medium">تاريخ الميلاد (ميلادي)</dt>
                        <dd className="text-neutral-900">{addDonorProfile.dob}</dd>
                      </div>
                    )}
                    {addDonorProfile?.dobHijri && (
                      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-4 py-3">
                        <dt className="text-neutral-500 text-sm font-medium">تاريخ الميلاد (هجري)</dt>
                        <dd className="text-neutral-900">{addDonorProfile.dobHijri}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                <div className="mb-5 flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="ack-donor"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-neutral-300 text-sa-600 focus:ring-sa-500"
                  />
                  <label htmlFor="ack-donor" className="text-sm text-neutral-700 cursor-pointer">
                    أقر بصحة البيانات المدخلة وأوافق على تفعيل حسابي بصفة متبرع.
                  </label>
                </div>

                {err && (
                  <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {err}
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  <button
                    type="submit"
                    disabled={busy || upgradeVerificationLoading || !acknowledged}
                    className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {busy ? "جاري إنشاء الحساب..." : "إنشاء حساب متبرع"}
                  </button>
                </div>
              </>
            )}
          </div>
        </form>
      )}

      {/* STEP 1 — Identity */}
      {step === "identity" && (
        <form onSubmit={onVerifyIdentity} className={cardCls}>
          <div className="p-5 lg:p-6">
            <div className="mb-4 text-center">
              <h2 className={sectionTitleCls}>التحقق من خلال خدمة يقين</h2>
            </div>

            {/* National ID */}
            <div className="mb-4">
              <label className={labelCls}>رقم الهوية الوطنية</label>
              <input
                className={inputBaseCls}
                inputMode="numeric"
                pattern="\d*"
                value={nationalId}
                onChange={(e) => setNationalId(e.target.value)}
                placeholder="1XXXXXXXXX أو 2XXXXXXXXX"
                aria-label="رقم الهوية الوطنية"
              />
            </div>

            {/* Citizen (Hijri yyyy-MM) vs Resident (Gregorian yyyy-MM-dd) */}
            {isCitizenId(nationalId) ? (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>سنة الميلاد (هجري)</label>
                    <select
                      className={inputBaseCls}
                      value={hijriY}
                      onChange={(e) => setHijriY(e.target.value)}
                    >
                      <option value="">اختر السنة</option>
                      {hijriYears.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>شهر الميلاد (هجري)</label>
                    <select
                      className={inputBaseCls}
                      value={hijriM}
                      onChange={(e) => setHijriM(e.target.value)}
                    >
                      <option value="">اختر الشهر</option>
                      {hijriMonths.map(([val, name]) => (
                        <option key={val} value={val}>
                          {`${val} - ${name}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className={helpCls}>سيتم إرسال السنة والشهر فقط (هجري)</p>
              </>
            ) : (
              <>
                <div className="mb-2">
                  <label className={labelCls}>تاريخ الميلاد (ميلادي)</label>
                  <input
                    type="date"
                    className={inputBaseCls}
                    value={gregDate}
                    onChange={(e) => setGregDate(e.target.value)}
                    aria-label="تاريخ الميلاد"
                  />
                </div>
                <p className={helpCls}>سيتم إرسال التاريخ الكامل (يوم/شهر/سنة)</p>
              </>
            )}

            {/* reCAPTCHA */}
            <div className="mt-4 flex justify-center">
              {RECAPTCHA_SITE_KEY ? (
                <ReCAPTCHA
                  ref={recaptcha1Ref}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(tok) => setCaptcha1(tok)}
                  onExpired={() => setCaptcha1(null)}
                  onErrored={() => setErr("حدث خطأ في reCAPTCHA. يرجى تحديث الصفحة")}
                  hl="ar"
                />
              ) : (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  مفاتيح reCAPTCHA غير متوفرة
                </div>
              )}
            </div>

            {/* Error */}
            {err && (
              <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                {err}
              </div>
            )}

            {/* Actions */}
            <div className="mt-5 flex justify-end">
              <button
                className="inline-flex items-center justify-center rounded-xl bg-sa-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:opacity-50"
                disabled={busy}
              >
                {busy ? "جاري التحقق..." : "تحقق"}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* STEP 2 — Phone + OTP */}
      {step === "phone" && (
        <form onSubmit={onSubmitSignup} className={cardCls}>
          <div className="p-5 lg:p-6">
            <div className="mb-4">
              <h2 className={sectionTitleCls}>معلومات الاتصال</h2>
            </div>

            {/* Verified banner */}
            <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              <div className="font-semibold">
                تم التحقق كـ: {verifiedName || "-"}
              </div>
              <div>رقم الهوية الوطنية: <strong>{onlyDigits(nationalId) || "-"}</strong></div>
              <div>المنطقة: <strong>{verifiedRegionName || "-"}</strong></div>
              <div>اسم البلد: <strong>{verifiedCountryName || "-"}</strong></div>
              <div>الجنس: <strong>{gender === "male" ? "ذكر" : gender === "female" ? "أنثى" : "-"}</strong></div>
              <div>
                تاريخ الميلاد (ميلادي): <strong>{dobGregorian || "-"}</strong>
              </div>
            </div>

            <p className="mb-3 text-xs text-gray-500">البيانات التالية من خدمة يقين ولا يمكن تعديلها.</p>

            <div className="mb-4">
              <label className={labelCls}>الاسم الكامل</label>
              <input
                type="text"
                className={inputReadOnlyCls}
                value={verifiedName || ""}
                readOnly
                dir="rtl"
                aria-readonly="true"
                placeholder="الاسم من يقين"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className={labelCls}>رقم الهوية الوطنية</label>
                <input
                  type="text"
                  className={inputReadOnlyCls}
                  value={onlyDigits(nationalId) || ""}
                  readOnly
                  dir="ltr"
                  aria-readonly="true"
                  placeholder="1XXXXXXXXX"
                />
              </div>
              <div>
                <label className={labelCls}>تاريخ الميلاد (ميلادي)</label>
                <input
                  type="text"
                  className={inputReadOnlyCls}
                  value={dobGregorian || ""}
                  readOnly
                  dir="ltr"
                  aria-readonly="true"
                  placeholder="yyyy-MM-dd"
                />
              </div>
              <div>
                <label className={labelCls}>المنطقة</label>
                <input
                  type="text"
                  className={inputReadOnlyCls}
                  value={verifiedRegionName || ""}
                  readOnly
                  dir="rtl"
                  aria-readonly="true"
                  placeholder="من يقين"
                />
              </div>
              <div>
                <label className={labelCls}>اسم البلد</label>
                <input
                  type="text"
                  className={inputReadOnlyCls}
                  value={verifiedCountryName || ""}
                  readOnly
                  dir="rtl"
                  aria-readonly="true"
                  placeholder="من يقين"
                />
              </div>
              <div>
                <label className={labelCls}>الجنس</label>
                <input
                  type="text"
                  className={inputReadOnlyCls}
                  value={gender === "male" ? "ذكر" : gender === "female" ? "أنثى" : "—"}
                  readOnly
                  dir="rtl"
                  aria-readonly="true"
                  placeholder="من يقين"
                />
                <p className={helpCls}>تم تحديد الجنس من رقم الهوية الوطنية ولا يمكن تغييره</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 mt-4">

              {/* Phone */}
              <div>
                <label className={labelCls}>رقم الهاتف</label>
                <input
                  className={inputBaseCls}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="05XXXXXXXX أو 9665XXXXXXXX"
                />
              </div>
            </div>

            {/* البريد الإلكتروني — مطلوب لإرسال رمز التحقق (السيرفر يتحقق منه) */}
            <div className="mt-4">
              <label className={labelCls}>البريد الإلكتروني</label>
              <input
                type="email"
                className={inputBaseCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </div>

            {/* reCAPTCHA #2 — يظهر فقط قبل إرسال OTP أو عند طلب إعادة الإرسال */}
            {(!otpSent || showResendSection) && (
              <div className="mt-4 flex justify-center">
                {RECAPTCHA_SITE_KEY ? (
                  <ReCAPTCHA
                    ref={recaptcha2Ref}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(tok) => setCaptcha2(tok)}
                    onExpired={() => setCaptcha2(null)}
                    onErrored={() => setErr("حدث خطأ في reCAPTCHA. يرجى تحديث الصفحة")}
                    hl="ar"
                  />
                ) : (
                  <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    مفاتيح reCAPTCHA غير متوفرة
                  </div>
                )}
              </div>
            )}

            {/* إرسال / إعادة إرسال رمز التحقق */}
            <div className="mt-4">
              {!otpSent ? (
                <>
                  <button
                    type="button"
                    onClick={onSendOtp}
                    disabled={busy || cooldown > 0 || !normPhoneKsa(phone) || !(email || "").trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim()) || !captcha2}
                    className="inline-flex items-center justify-center w-full rounded-xl border border-sa-300 bg-white px-4 py-2 text-sm font-semibold text-sa-700 shadow-sm hover:bg-sa-50 focus:outline-none focus:ring-2 focus:ring-sa-500 disabled:opacity-50"
                  >
                    {busy ? "جاري الإرسال..." : "إرسال رمز التحقق إلى البريد الإلكتروني"}
                  </button>
                  <p className={helpCls}>سيتم إرسال رمز التحقق إلى البريد الإلكتروني المدخل أعلاه</p>
                </>
              ) : (
                <>
                  {!showResendSection ? (
                    <button
                      type="button"
                      onClick={() => setShowResendSection(true)}
                      className="text-sm text-sa-600 hover:text-sa-700 underline"
                    >
                      لم تصلك الرسالة؟ إعادة إرسال رمز التحقق
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={onSendOtp}
                      disabled={busy || cooldown > 0 || !captcha2}
                      title={cooldown > 0 ? `إعادة الإرسال خلال ${cooldown} ثانية` : undefined}
                      className="inline-flex items-center justify-center w-full rounded-xl border border-sa-300 bg-white px-4 py-2 text-sm font-semibold text-sa-700 shadow-sm hover:bg-sa-50 focus:outline-none focus:ring-2 focus:ring-sa-500 disabled:opacity-50"
                    >
                      {cooldown > 0 ? `إعادة الإرسال خلال ${cooldown} ثانية` : "إعادة إرسال رمز التحقق"}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* رمز التحقق — يظهر فقط بعد الضغط على إرسال رمز التحقق */}
            {otpSent && (
              <div className="mt-4">
                <label className={labelCls}>رمز التحقق</label>
                <input
                  className={inputBaseCls}
                  placeholder="••••••"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={6}
                  dir="ltr"
                />
                <p className={helpCls}>أدخل رمز التحقق المرسل إلى رقم هاتفك (من 1 إلى 6 أرقام)</p>
              </div>
            )}

            {/* Error */}
            {err && (
              <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                {err}
              </div>
            )}

            {/* Actions */}
            <div className="mt-5 flex justify-end">
              <button
                className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50"
                disabled={busy || !otpSent || !otp || !/^\d{1,6}$/.test(otp.trim()) || !normPhoneKsa(phone) || !(email || "").trim()}
              >
                {busy ? "جاري الحفظ..." : "إنشاء الحساب"}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* STEP 3 — Done */}
      {step === "done" && (
        <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5 text-emerald-800">
          تم إنشاء الحساب بنجاح! سيتم توجيهك إلى صفحة الأراضي المتاحة قريبًا...
        </div>
      )}
    </main>
  );
}
